import React from 'react';
import { 
    Container, 
    Typography, 
    Box, 
    Card, 
    CardContent, 
    Stack, 
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Divider,
    Paper,
    Button
} from '@mui/material';
import { 
    Quiz, 
    Person, 
    Score, 
    TipsAndUpdates, 
    EmojiEvents,
    Schedule,
    Share,
    Home
} from '@mui/icons-material';

const HowToPlay: React.FC = () => {
    return (
        <Container maxWidth="md" sx={{ py: { xs: 0.25, sm: 0.2, md: 1.5 }, px: { xs: 0.25, sm: 0.2, md: 0.75 } }}>
            <Typography variant="h3" component="h1" gutterBottom align="center" color="primary">
                How to Play Famous Initials
            </Typography>

            {/* Game Overview */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                        <Person color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h5" component="h2">
                            The Challenge
                        </Typography>
                    </Box>
                    <Typography variant="body1" paragraph>
                        Every day, we feature a new famous person. Your goal is to identify them using only their initials.
                        Reveal clues by asking questions. 
                    </Typography>
                    <Typography variant="body1">
                        We use the name they are most famous for, not necessarily their real name.
                        For example, BP stands for "Brad Pitt," instead of his real name, "William Bradley Pitt."
                        They can be a real person or a fictional character!
                    </Typography>
                </CardContent>
            </Card>

            {/* How to Play Steps */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" mb={1}>
                        <Quiz color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h5" component="h2">
                            How to Play
                        </Typography>
                    </Box>
                    <List sx={{ '& .MuiListItem-root:last-child': { mb: 0 }, '& .MuiListItem-root': { py: 0.5 } }}>
                        <ListItem>
                            <ListItemIcon>
                                <Typography variant="h6" color="primary">1</Typography>
                            </ListItemIcon>
                            <ListItemText 
                                primary="Ask Questions"
                                secondary="Click 'Show Answer' to reveal clues about the person."
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <Typography variant="h6" color="primary">2</Typography>
                            </ListItemIcon>
                            <ListItemText
                                primary="Make Your Guess"
                                secondary="Enter their most famous name."
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <Typography variant="h6" color="primary"></Typography>
                            </ListItemIcon>
                            <ListItemText
                                secondary="💡 Tip: Check your spelling."
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <Typography variant="h6" color="primary">3</Typography>
                            </ListItemIcon>
                            <ListItemText 
                                primary="Submit & Win!"
                                secondary="Click 'Guess' to submit your answer."
                            />
                        </ListItem>
                    </List>
                </CardContent>
            </Card>

            {/* Question Types */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                        <TipsAndUpdates color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h5" component="h2">
                            Types of Clues
                        </Typography>
                    </Box>
                    
                    <Stack spacing={2}>
                        <Paper elevation={1} sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom color="primary">
                                Questions
                            </Typography>
                            <Typography variant="body2" color="text.secondary" paragraph>
                                Available for every person. Use these in any order to narrow down your search:
                            </Typography>
                            <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mt: 1 }}>
                                <Chip label="What are they famous for?" size="small" />
                                <Chip label="Age category" size="small" />
                                <Chip label="Gender" size="small" />
                                <Chip label="Nationality" size="small" />
                                <Chip label="Family status" size="small" />
                                <Chip label="Fictional or real" size="small" />
                            </Stack>
                        </Paper>

                        <Paper elevation={1} sx={{ p: 2 }}>
                            <Typography variant="h6" gutterBottom color="success.main">
                                Trivia
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Fun facts about the person that become available after all questions are exhausted.
                            </Typography>
                        </Paper>
                    </Stack>
                </CardContent>
            </Card>

            {/* Scoring System */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                        <Score color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h5" component="h2">
                            Scoring System (Golf Style)
                        </Typography>
                    </Box>
                    <List sx={{ '& .MuiListItem-root': { py: 0.5 } }}>
                        <ListItem>
                            <ListItemIcon>
                                <EmojiEvents color="warning" />
                            </ListItemIcon>
                            <ListItemText 
                                primary="Lower Score = Better Performance"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <Typography variant="body1">+10</Typography>
                            </ListItemIcon>
                            <ListItemText 
                                primary="Each Question Costs 10 Points"
                            />
                        </ListItem>
                        <ListItem>
                            <ListItemIcon>
                                <Typography variant="body1">+10</Typography>
                            </ListItemIcon>
                            <ListItemText 
                                primary="Each Guess (correct or incorrect) Costs 10 Points"
                            />
                        </ListItem>
                    </List>
                </CardContent>
            </Card>

            {/* Daily Game */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" mb={2}>
                        <Schedule color="primary" sx={{ mr: 1 }} />
                        <Typography variant="h5" component="h2">
                            Daily Challenge
                        </Typography>
                    </Box>
                    <Typography variant="body1" paragraph>
                        A new famous person is featured every day. Come back daily to test your knowledge 
                        and improve your guessing skills!
                    </Typography>
                    <Box display="flex" alignItems="center" mt={2}>
                        <Share color="primary" sx={{ mr: 1 }} />
                        <Typography variant="body2" color="text.secondary">
                            Share your scores with friends and see who can guess with the fewest clues!
                        </Typography>
                    </Box>
                </CardContent>
            </Card>

            <Divider sx={{ my: 3 }} />

            <Box textAlign="center">
                <Button 
                    variant="contained" 
                    size="large" 
                    startIcon={<Home />}
                    href="/"
                    sx={{ px: 4, py: 1.5 }}
                >
                    Back to Game
                </Button>
            </Box>
        </Container>
    );
};

export default HowToPlay;